import {useMutation, useQuery, useQueryClient} from '@github-ui/react-query'
import {verifiedFetchJSON} from '@github-ui/verified-fetch'

export type TriageAgentPayload = {
  enabled: boolean
  default_branch: string
  config_exists: boolean
  config_valid: boolean | null
  config_path: string
  viewer_can_configure: boolean
}

function getQueryKey(apiUrl: string) {
  return ['triage-agent-status', apiUrl]
}

class TriageAgentUpdateError extends Error {
  status: number
  error?: string
  constructor(message: string, status: number, error?: string) {
    super(message)
    this.name = 'TriageAgentUpdateError'
    this.status = status
    this.error = error
  }
}

export function useTriageAgentStatus(repoOwner: string, repoName: string) {
  const apiUrl = `/${repoOwner}/${repoName}/triage-agent`

  const {data, isLoading, isError} = useQuery({
    queryKey: getQueryKey(apiUrl),
    queryFn: async () => {
      const response = await verifiedFetchJSON(apiUrl)
      if (!response.ok) {
        throw new Error('Failed to fetch triage agent status')
      }
      return response.json() as Promise<TriageAgentPayload>
    },
    // Don't keep stale data after unmount — config_valid can change
    // between navigations, and showing the previous state briefly
    // before the refetch completes is confusing.
    gcTime: 0,
  })

  return {
    data,
    isLoading,
    isError,
  }
}

export function useUpdateTriageAgentStatus(repoOwner: string, repoName: string) {
  const queryClient = useQueryClient()
  const apiUrl = `/${repoOwner}/${repoName}/triage-agent`

  const {mutate, isPending, isError, reset} = useMutation({
    mutationFn: async (enabled: boolean) => {
      const response = await verifiedFetchJSON(apiUrl, {
        method: 'PATCH',
        body: {enabled},
      })
      if (!response.ok) {
        let body: {error?: string; message?: string} = {}
        try {
          body = await response.json()
        } catch {
          // ignore parse errors
        }
        throw new TriageAgentUpdateError(
          body?.message ?? 'Failed to update triage agent status',
          response.status,
          body?.error,
        )
      }
      return response.json() as Promise<TriageAgentPayload>
    },
    onMutate: async (newEnabled: boolean) => {
      // Cancel any outgoing refetches
      await queryClient.cancelQueries({queryKey: getQueryKey(apiUrl)})

      // Snapshot the previous value
      const previousData = queryClient.getQueryData<TriageAgentPayload>(getQueryKey(apiUrl))

      // Optimistically update to the new value, preserving existing fields
      queryClient.setQueryData<TriageAgentPayload>(getQueryKey(apiUrl), oldData =>
        oldData ? {...oldData, enabled: newEnabled} : oldData,
      )

      // Return a context object with the snapshotted value
      return {previousData}
    },
    onError: (err, _newEnabled, context) => {
      // Roll back to the previous value on error
      if (context?.previousData) {
        queryClient.setQueryData(getQueryKey(apiUrl), context.previousData)
      }

      // If config is required, update the cached data to reflect config_exists: false
      if (err instanceof TriageAgentUpdateError && err.status === 422 && err.error === 'config_required') {
        queryClient.setQueryData<TriageAgentPayload>(getQueryKey(apiUrl), oldData =>
          oldData ? {...oldData, config_exists: false, config_valid: null, enabled: false} : oldData,
        )
      }
    },
    onSettled: () => {
      // Refetch to sync with server state
      queryClient.invalidateQueries({queryKey: getQueryKey(apiUrl)})
    },
  })

  return {
    updateStatus: mutate,
    isPending,
    isError,
    reset,
  }
}
